import React, { useState, useEffect } from 'react';
import { Card, Table, DatePicker, Button, Space, Typography, message, Input } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface PaymentEntry {
  id: number;
  entryDate: string;
  accountId: number;
  accountName: string;
  accountCode: string;
  lastVehiclePlate?: string | null;
  lastVehicleBrandModel?: string | null;
  amount: number;
  description: string | null;
  receiptNo: string | null;
  serviceOrderId: number | null;
  serviceOrderNo: string | null;
  isServiceOrderPayment: boolean;
  createdAt: string;
}

interface ReportResponse {
  entries: PaymentEntry[];
  total: number;
  count: number;
}

const PaymentReport: React.FC = () => {
  const isMobile = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportResponse | null>(null);
  const [accountSearch, setAccountSearch] = useState<string>('');
  const [plateSearch, setPlateSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() => {
    const start = dayjs().startOf('month');
    const end = dayjs().endOf('month');
    return [start, end];
  });

  const loadReport = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');
      if (accountSearch.trim()) params.accountName = accountSearch.trim();
      if (plateSearch.trim()) params.plate = plateSearch.trim();
      const { data: res } = await api.get<ReportResponse>('/payments/report', { params });
      setData(res);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ödemeler dökümü yüklenirken hata');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const columns = [
    { title: 'Tarih', dataIndex: 'entryDate', key: 'entryDate', width: 110, render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-' },
    { title: 'Cari kodu', dataIndex: 'accountCode', key: 'accountCode', width: 100 },
    { title: 'Cari', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Plaka', dataIndex: 'lastVehiclePlate', key: 'lastVehiclePlate', width: 120, render: (v: string | null) => v ?? '-' },
    { title: 'Araç', dataIndex: 'lastVehicleBrandModel', key: 'lastVehicleBrandModel', width: 180, ellipsis: true, render: (v: string | null) => v ?? '-' },
    { title: 'Tutar (₺)', dataIndex: 'amount', key: 'amount', width: 120, render: (v: number) => v?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) ?? '-' },
    { title: 'Makbuz no', dataIndex: 'receiptNo', key: 'receiptNo', width: 140 },
    { title: 'Servis fişi', dataIndex: 'serviceOrderNo', key: 'serviceOrderNo', width: 110, render: (v: string | null) => v ?? '-' },
    { title: 'Açıklama', dataIndex: 'description', key: 'description', ellipsis: true, render: (v: string | null) => v ?? '-' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>Ödemeler dökümü</Title>
          <Space>
            <Input
              placeholder="Cari adı / soyadı"
              allowClear
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              style={{ width: 200 }}
            />
            <Input
              placeholder="Plaka"
              allowClear
              value={plateSearch}
              onChange={(e) => setPlateSearch(e.target.value)}
              style={{ width: 140 }}
            />
            <RangePicker
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              format="DD.MM.YYYY"
            />
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadReport} loading={loading}>
              Listele
            </Button>
          </Space>
        </Space>
        {data != null && (
          <Space style={{ marginBottom: 16 }}>
            <Text strong>Toplam: {data.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</Text>
            <Text type="secondary">({data.count} kayıt)</Text>
          </Space>
        )}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={data?.entries ?? []}
          columns={columns}
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, showSizeChanger: !isMobile, showTotal: (t) => `Toplam ${t} kayıt` }}
        />
      </Card>
    </div>
  );
};

export default PaymentReport;
